import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import DialogWithDontAskAgainActions from './DialogWithDontAskAgainActions';
import QRCode from 'react-qr-code';
import Barcode from 'react-barcode';
import useDisplayFormat from 'hooks/useDisplayFormat';
import styled from 'styled-components';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';

type ConfirmationDialogProps = {
    open: boolean;
    onClose: () => void;
    title: string;
    content: string;
    checked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    onCancel: () => void;
    onConfirm: () => void;
    cancelLabel?: string;
    confirmLabel?: string;
    // When provided, renders a scannable code at the bottom of the dialog
    scannerCode?: string;
    scannerCodeLabel?: string;
};

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    open,
    onClose,
    title,
    content,
    checked,
    onCheckedChange,
    onCancel,
    onConfirm,
    cancelLabel,
    confirmLabel,
    scannerCode,
    scannerCodeLabel,
}) => {
    const { format } = useDisplayFormat();
    const { t } = useTranslation();

    const renderScannerCode = () => {
        if (!scannerCode) return null;
        return (
            <ScannerCodeSection column align="center">
                <Flex gap="0.5rem" align="center" className="w-100">
                    <FontAwesomeIcon icon={faCircleInfo} size="1x" color="#0011ff" />
                    <ScannerCodeLabel>{scannerCodeLabel}</ScannerCodeLabel>
                </Flex>
                <ScannerSubText>{t('scannerScanAgainConfirm')}</ScannerSubText>
                <Flex column align="center" gap="0.75rem" className="w-100">
                    {format === 'qrcode' ? (
                        <QRCode value={scannerCode} size={140} />
                    ) : (
                        <BarcodeWrapper>
                            <Barcode
                                value={scannerCode}
                                height={60}
                                fontSize={11}
                                margin={0}
                                text={scannerCodeLabel ?? scannerCode}
                            />
                        </BarcodeWrapper>
                    )}
                </Flex>
            </ScannerCodeSection>
        );
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>{content}</DialogContent>
            {!scannerCode && (
                <DialogActions>
                    <DialogWithDontAskAgainActions
                        checked={checked}
                        onCheckedChange={onCheckedChange}
                        onCancel={onCancel}
                        onConfirm={onConfirm}
                        cancelLabel={cancelLabel}
                        confirmLabel={confirmLabel}
                    />
                </DialogActions>
            )}
            {scannerCode && renderScannerCode()}
        </Dialog>
    );
};

export default ConfirmationDialog;

const ScannerCodeSection = styled(Flex)`
    padding: 0.75rem 1rem 1rem;
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
    gap: 0.5rem;
`;

const ScannerCodeLabel = styled.label`
    color: ${({ theme }) => theme.colors.text};
    font-size: 1.25rem;
    font-weight: 700;
    margin-top: 0;
`;

const ScannerSubText = styled(Text)`
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.text};
`;

const BarcodeWrapper = styled.div`
    width: 100%;
    svg {
        width: 100% !important;
        height: auto !important;
    }
    @media print {
        width: 40vmin;
    }
`;
